/**
 * 图层蒙版与剪贴蒙版命令（M2-13 / DESIGN.md §4.2、§4.4 第 5/7 步）。
 *
 * 蒙版像素与图层像素一样**只住在 TileStore**（CLAUDE.md 规则 6）：
 * 蒙版有自己的图层 id（{@link maskLayerIdFor}），RasterMask 里只有 TileHandle。
 * 值的存储约定：**alpha 通道**为蒙版值（R/G/B 同步写同值便于调试预览），
 * 未分配块按 `base`（稀疏：「全部显示」的默认蒙版零分配）。
 *
 * 结构性命令（加/删/停用/剪贴切换）走 CommandBus；像素操作（由选区生成、
 * 反相）走 PixelTransaction——与画笔同一套混合历史策略（M1-08）。
 */
import type { Command } from '../command-bus';
import { findLayer } from '../../document/draft';
import type { Document, RasterMask } from '../../document/types';
import { TILE, layerId as makeLayerId, tileKey, type LayerId } from '../../engine/tile/key';
import { BYTES_PER_TILE, type TileStore } from '../../engine/tile/store';
import type { SelectionMask } from '../../engine/selection/mask';
import type { StrokeTransaction } from '../../engine/brush/buffer';

const STRUCTURAL_COST = 128;

/** 蒙版像素的图层 id。`#` 不会出现在用户可见的图层名里（id 是内部值）。 */
export function maskLayerIdFor(layer: LayerId | string): LayerId {
  return makeLayerId(`${layer}#mask`);
}

function maskOf(document: Document, id: LayerId): RasterMask | undefined {
  return findLayer(document.root, id)?.layer.mask;
}

/* ------------------------------------------------------------------ */
/* 结构性命令                                                            */
/* ------------------------------------------------------------------ */

export function createAddMaskCommand(id: LayerId, mask: RasterMask): Command {
  return {
    kind: 'mask.add',
    label: '添加图层蒙版',
    cost: STRUCTURAL_COST,
    apply(draft) {
      draft.updateLayer(id, (layer) => ({ ...layer, mask }));
    },
    invert(document) {
      const previous = maskOf(document, id);
      return previous === undefined
        ? createRemoveMaskCommand(id)
        : createAddMaskCommand(id, previous);
    },
  };
}

export function createRemoveMaskCommand(id: LayerId): Command {
  return {
    kind: 'mask.remove',
    label: '删除图层蒙版',
    cost: STRUCTURAL_COST,
    record: { kind: 'mask.remove', args: { id } },
    apply(draft) {
      draft.updateLayer(id, (layer) => {
        const { mask, ...rest } = layer;
        void mask;
        return rest as typeof layer;
      });
    },
    invert(document) {
      const previous = maskOf(document, id);
      // 没有蒙版时删除是空操作，逆操作同样是空操作
      return previous === undefined ? createRemoveMaskCommand(id) : createAddMaskCommand(id, previous);
    },
  };
}

export function createSetMaskEnabledCommand(id: LayerId, enabled: boolean): Command {
  return {
    kind: 'mask.enable',
    label: enabled ? '启用图层蒙版' : '停用图层蒙版',
    cost: STRUCTURAL_COST,
    record: { kind: 'mask.enable', args: { id, enabled } },
    apply(draft) {
      draft.updateLayer(id, (layer) =>
        layer.mask === undefined ? layer : { ...layer, mask: { ...layer.mask, enabled } },
      );
    },
    invert(document) {
      const previous = maskOf(document, id);
      return createSetMaskEnabledCommand(id, previous?.enabled ?? true);
    },
  };
}

/** 剪贴蒙版切换（图层面板 Alt+点击分界线的语义，§4.4 第 7 步）。 */
export function createSetClippedCommand(id: LayerId, clipped: boolean): Command {
  return {
    kind: 'layer.clip',
    label: clipped ? '创建剪贴蒙版' : '释放剪贴蒙版',
    cost: STRUCTURAL_COST,
    record: { kind: 'layer.clip', args: { id, clipped } },
    apply(draft) {
      draft.updateLayer(id, (layer) => ({ ...layer, clipped }));
    },
    invert(document) {
      const previous = findLayer(document.root, id)?.layer.clipped ?? false;
      return createSetClippedCommand(id, previous);
    },
  };
}

/** 蒙版翻转 base 值（像素反相的结构半步；另一半是 invertMaskPixels）。 */
export function createFlipMaskBaseCommand(id: LayerId): Command {
  return {
    kind: 'mask.flipBase',
    label: '反相图层蒙版',
    cost: STRUCTURAL_COST,
    record: { kind: 'mask.flipBase', args: { id } },
    apply(draft) {
      draft.updateLayer(id, (layer) =>
        layer.mask === undefined
          ? layer
          : {
              ...layer,
              mask: { ...layer.mask, base: ((layer.mask.base ?? 255) === 255 ? 0 : 255) as 0 | 255 },
            },
      );
    },
    invert() {
      return createFlipMaskBaseCommand(id); // 自逆
    },
  };
}

/* ------------------------------------------------------------------ */
/* 像素操作                                                             */
/* ------------------------------------------------------------------ */

/**
 * 由当前选区生成蒙版像素，返回可挂到图层上的 RasterMask。
 * 只写选区偏离其 base 的块——「全选生成的显示全部蒙版」零分配。
 */
export function writeMaskFromSelection(
  store: TileStore,
  selection: SelectionMask,
  layer: LayerId,
  transaction: StrokeTransaction,
): RasterMask {
  const maskId = maskLayerIdFor(layer);
  const coords = [...selection.tileCoords()];
  for (const { tx, ty } of coords) transaction.touch(tileKey(maskId, tx, ty));
  transaction.capture();

  for (const { tx, ty } of coords) {
    const source = selection.tile(tx, ty)!;
    const out = new Uint8Array(BYTES_PER_TILE);
    for (let i = 0; i < TILE * TILE; i++) {
      const v = source[i]!;
      out[i * 4] = v;
      out[i * 4 + 1] = v;
      out[i * 4 + 2] = v;
      out[i * 4 + 3] = v;
    }
    store.set(tileKey(maskId, tx, ty), out);
  }

  const bounds = selection.contentBounds() ?? {
    x: 0,
    y: 0,
    width: selection.width,
    height: selection.height,
  };
  return {
    tiles: store.handle(maskId),
    bounds,
    linked: true,
    enabled: true,
    base: selection.base,
  };
}

/** 反相蒙版的像素半步：已分配块逐像素 255−v。base 翻转走 createFlipMaskBaseCommand。 */
export function invertMaskPixels(
  store: TileStore,
  layer: LayerId,
  transaction: StrokeTransaction,
): Array<{ tx: number; ty: number }> {
  const maskId = maskLayerIdFor(layer);
  const coords: Array<{ tx: number; ty: number }> = [];
  for (const tile of store.residentTilesOf(maskId, 0)) {
    coords.push({ tx: tile.key.tx, ty: tile.key.ty });
  }
  for (const { tx, ty } of coords) transaction.touch(tileKey(maskId, tx, ty));
  transaction.capture();

  for (const { tx, ty } of coords) {
    const key = tileKey(maskId, tx, ty);
    const existing = store.get(key)!.data!;
    const out = new Uint8Array(BYTES_PER_TILE);
    for (let i = 0; i < out.length; i++) out[i] = 255 - existing[i]!;
    store.set(key, out);
  }
  return coords;
}
